/**
 * Transaction React Query Hooks
 *
 * Provides data fetching and mutation hooks for transaction-related operations.
 */

import type { ExpenseAllocationsUpdate, PaginatedResponse, ReimbursementAccept, ReimbursementMark, TransactionCreate, TransactionUpdate, TransactionWithCategory } from '@/shared/types';
import { hasNextPage } from '@/shared/types';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createDetailHooks } from '@/shared/hooks/create-detail-hooks';
import { reportKeys } from '@/features/reports/hooks/use-reports';
import {
  transactionService,
  type TransactionListParams,
} from '../services/transaction-service';

/**
 * Query key factory for transaction queries
 */
export const transactionKeys = {
  all: ['transactions'] as const,
  lists: () => [...transactionKeys.all, 'list'] as const,
  list: (params?: TransactionListParams) => [...transactionKeys.lists(), params] as const,
  infiniteList: (params?: Omit<TransactionListParams, 'page'>) =>
    [...transactionKeys.lists(), 'infinite', params] as const,
  details: () => [...transactionKeys.all, 'detail'] as const,
  detail: (id: string) => [...transactionKeys.details(), id] as const,
  allocations: (id: string) => [...transactionKeys.detail(id), 'allocations'] as const,
  receipts: (id: string) => [...transactionKeys.detail(id), 'receipts'] as const,
  activity: (id: string) => [...transactionKeys.detail(id), 'activity'] as const,
};

/**
 * Hook to fetch paginated list of transactions
 *
 * @param params - Optional filters and pagination
 * @returns Query result with paginated transactions
 */
export function useTransactions(params?: TransactionListParams) {
  return useQuery({
    queryKey: transactionKeys.list(params),
    queryFn: () => transactionService.list(params),
    staleTime: 30 * 1000, // High volatility — transactions change frequently
  });
}

/**
 * Hook to fetch infinite-scrolling list of transactions
 *
 * Uses React Query's useInfiniteQuery to accumulate pages as the user
 * clicks "Load More", rather than replacing the current page.
 *
 * @param params - Optional filters (page is managed internally)
 * @returns Infinite query result with accumulated transaction pages
 */
export function useInfiniteTransactions(params?: Omit<TransactionListParams, 'page'>) {
  return useInfiniteQuery<PaginatedResponse<TransactionWithCategory>>({
    queryKey: transactionKeys.infiniteList(params),
    queryFn: ({ pageParam }) =>
      transactionService.list({ ...params, page: pageParam as number }),
    getNextPageParam: (lastPage) => (hasNextPage(lastPage) ? lastPage.page + 1 : undefined),
    initialPageParam: 1,
  });
}

/**
 * useTransaction(id?) — conditional fetch, data may be undefined.
 * useTransactionSuspense(id) — suspense fetch, data is guaranteed. Must be inside <Suspense>.
 */
export const [useTransaction, useTransactionSuspense] = createDetailHooks(
  transactionKeys.detail,
  transactionService.getById,
);

/**
 * Hook to create a new transaction
 *
 * @returns Mutation for creating transactions
 */
export function useCreateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TransactionCreate) => transactionService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: transactionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['budgets'] });
      queryClient.invalidateQueries({ queryKey: ['balances'] });
      queryClient.invalidateQueries({ queryKey: reportKeys.all });
    },
  });
}

/**
 * Hook to update a transaction
 *
 * @returns Mutation for updating transactions
 */
export function useUpdateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TransactionUpdate }) =>
      transactionService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: transactionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: transactionKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: reportKeys.all });
      // Invalidate balances/budgets when split_type, category, or exclusion changes
      if (variables.data.split_type !== undefined || variables.data.category_id !== undefined || variables.data.excluded_from_totals !== undefined) {
        queryClient.invalidateQueries({ queryKey: ['balances'] });
        queryClient.invalidateQueries({ queryKey: ['budgets'] });
      }
    },
  });
}

/**
 * Hook to delete a transaction
 *
 * @returns Mutation for deleting transactions
 */
export function useDeleteTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => transactionService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: transactionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['budgets'] });
      queryClient.invalidateQueries({ queryKey: ['balances'] });
      queryClient.invalidateQueries({ queryKey: reportKeys.all });
    },
  });
}

// =============================================================================
// Reimbursement
// =============================================================================

/**
 * Hook to mark a transaction as a reimbursement (Phase 1 — notification flow)
 *
 * @returns Mutation that marks a transaction as sent to a household member
 */
export function useMarkReimbursement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ transactionId, data }: { transactionId: string; data: ReimbursementMark }) =>
      transactionService.markReimbursement(transactionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: transactionKeys.all });
      queryClient.invalidateQueries({ queryKey: ['settlements'] });
      queryClient.invalidateQueries({ queryKey: ['balances'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/**
 * Hook to accept a reimbursement request (Phase 2 — links transaction + creates settlement)
 *
 * @returns Mutation for accepting a reimbursement
 */
export function useAcceptReimbursement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ transactionId, data }: { transactionId: string; data: ReimbursementAccept }) =>
      transactionService.acceptReimbursement(transactionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: transactionKeys.all });
      queryClient.invalidateQueries({ queryKey: ['settlements'] });
      queryClient.invalidateQueries({ queryKey: ['balances'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/**
 * Hook to decline a reimbursement request
 *
 * @returns Mutation for declining a reimbursement
 */
export function useDeclineReimbursement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (transactionId: string) =>
      transactionService.declineReimbursement(transactionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: transactionKeys.all });
      queryClient.invalidateQueries({ queryKey: ['settlements'] });
      queryClient.invalidateQueries({ queryKey: ['balances'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/**
 * Hook to undo a reimbursement marking on a transaction (sender cancels)
 *
 * @returns Mutation that undoes reimbursement state
 */
export function useUndoReimbursement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (transactionId: string) =>
      transactionService.undoReimbursement(transactionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: transactionKeys.all });
      queryClient.invalidateQueries({ queryKey: ['settlements'] });
      queryClient.invalidateQueries({ queryKey: ['balances'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

// =============================================================================
// Activity
// =============================================================================

/**
 * useTransactionActivity(id?) — conditional fetch, data may be undefined.
 * useTransactionActivitySuspense(id) — suspense fetch, data is guaranteed. Must be inside <Suspense>.
 */
export const [useTransactionActivity, useTransactionActivitySuspense] = createDetailHooks(
  transactionKeys.activity,
  transactionService.getActivity,
);

// =============================================================================
// Allocations
// =============================================================================

/**
 * useTransactionAllocations(id?) — conditional fetch, data may be undefined.
 * useTransactionAllocationsSuspense(id) — suspense fetch, data is guaranteed. Must be inside <Suspense>.
 */
export const [useTransactionAllocations, useTransactionAllocationsSuspense] = createDetailHooks(
  transactionKeys.allocations,
  transactionService.getAllocations,
);

/**
 * Hook to set expense allocations for a transaction
 *
 * @returns Mutation for setting allocations
 */
export function useSetTransactionAllocations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ExpenseAllocationsUpdate }) =>
      transactionService.setAllocations(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: transactionKeys.allocations(variables.id) });
      queryClient.invalidateQueries({ queryKey: transactionKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: ['balances'] });
    },
  });
}

// =============================================================================
// Receipts
// =============================================================================

/**
 * useTransactionReceipts(id?) — conditional fetch, data may be undefined.
 * useTransactionReceiptsSuspense(id) — suspense fetch, data is guaranteed. Must be inside <Suspense>.
 */
export const [useTransactionReceipts, useTransactionReceiptsSuspense] = createDetailHooks(
  transactionKeys.receipts,
  transactionService.getReceipts,
);

/**
 * Hook to upload a receipt for a transaction
 *
 * @returns Mutation for uploading receipts
 */
export function useUploadReceipt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ transactionId, file }: { transactionId: string; file: File }) =>
      transactionService.uploadReceipt(transactionId, file),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: transactionKeys.receipts(variables.transactionId),
      });
    },
  });
}

/**
 * Hook to delete a receipt
 *
 * @returns Mutation for deleting receipts
 */
export function useDeleteReceipt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      transactionId,
      receiptId,
    }: {
      transactionId: string;
      receiptId: string;
    }) => transactionService.deleteReceipt(transactionId, receiptId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: transactionKeys.receipts(variables.transactionId),
      });
    },
  });
}
